import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import { expertService, bookingService } from '../api/services';
import useApi from '../hooks/useApi';
import { Star, Clock, Video, Mic, MessageCircle, AlertCircle, ChevronLeft, Calendar } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';

const SESSION_TYPES = [
  { id: 'VIDEO', icon: Video,         label: 'Video Call' },
  { id: 'AUDIO', icon: Mic,           label: 'Audio Call' },
  { id: 'CHAT',  icon: MessageCircle, label: 'Chat Session' },
];

// ── Availability helpers ──────────────────────────────────────
const DAY_NAMES = ['sunday','monday','tuesday','wednesday','thursday','friday','saturday'];

const parseAvailability = (raw) => {
  if (!raw) return {};
  if (typeof raw === 'object' && !Array.isArray(raw)) return raw;
  try { return JSON.parse(raw); } catch { return {}; }
};

const parseSessionTypes = (raw) => {
  if (Array.isArray(raw)) return raw;
  try { return raw ? JSON.parse(raw) : ['VIDEO', 'AUDIO', 'CHAT']; } catch { return ['VIDEO', 'AUDIO', 'CHAT']; }
};

const getAvailableDayIndices = (availability) => {
  return DAY_NAMES.reduce((acc, day, idx) => {
    const slots = availability[day] ?? [];
    if (slots.length > 0) acc.push(idx);
    return acc;
  }, []);
};

const getTimeSlotsForDate = (date, availability) => {
  if (!date) return [];
  const dayName = DAY_NAMES[new Date(date).getDay()];
  const slots   = availability[dayName] ?? [];
  if (!slots.length) return [];

  const times = [];
  const now   = new Date();

  slots.forEach(({ start, end }) => {
    if (!start || !end) return;
    const [sh, sm] = start.split(':').map(Number);
    const [eh, em] = end.split(':').map(Number);
    let cur = sh * 60 + sm;
    const endMin = eh * 60 + em;

    while (cur + 30 <= endMin) {
      const h  = String(Math.floor(cur / 60)).padStart(2, '0');
      const m  = String(cur % 60).padStart(2, '0');
      const dt = new Date(`${date}T${h}:${m}`);
      if (dt > new Date(now.getTime() + 3600000)) {
        times.push(`${h}:${m}`);
      }
      cur += 30;
    }
  });

  return times;
};

const getNextAvailableDates = (availability, daysAhead = 60) => {
  const available = getAvailableDayIndices(availability);
  if (!available.length) return [];
  const dates = [];
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  for (let i = 1; i <= daysAhead; i++) {
    const d = new Date(today);
    d.setDate(today.getDate() + i);
    if (available.includes(d.getDay())) {
      dates.push(d.toISOString().split('T')[0]);
    }
  }
  return dates;
};

// ── Booking Modal ─────────────────────────────────────────────
const SESSION_FOCUS_CATEGORIES = [
  'Concept Development', 'Product Validation', 'MVP Development',
  'Market Strategy', 'Commercialisation', 'Partnerships & Channels',
  'Digital Presence', 'Revenue Optimization', 'Scaling & Diversification',
  'Operations & Systems', 'Investment Readiness', 'Customer Experience',
  'Regulatory Compliance', 'Supply Chain Development',
];

const BookingModal = ({ expert, availability, expertSessionTypes, onClose }) => {
  const { user } = useAuth();

  // Only show session types the expert actually offers
  const offeredTypes = SESSION_TYPES.filter(t => expertSessionTypes.includes(t.id));

  const [form, setForm] = useState({
    sessionType: offeredTypes[0]?.id ?? 'VIDEO',
    date: '', time: '', duration: 60, notes: '', sessionFocus: '',
  });
  const [loading, setLoading] = useState(false);
  const upd  = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const name = `${expert.user?.firstName ?? ''} ${expert.user?.lastName ?? ''}`.trim();

  const parsedAvail   = parseAvailability(availability);
  const availDates    = getNextAvailableDates(parsedAvail);
  const timeSlots     = getTimeSlotsForDate(form.date, parsedAvail);
  const hasAvail      = Object.values(parsedAvail).some(s => Array.isArray(s) && s.length > 0);
  const availDayNames = getAvailableDayIndices(parsedAvail)
    .map(i => DAY_NAMES[i].charAt(0).toUpperCase() + DAY_NAMES[i].slice(1));

  const handleDateChange = (e) => {
    setForm(f => ({ ...f, date: e.target.value, time: '' }));
  };

  const handleBook = async (e) => {
    e.preventDefault();
    if (!user) { toast.error('Please sign in to book a session'); return; }
    if (!form.date || !form.time) { toast.error('Please select a date and time'); return; }

    const dayName = DAY_NAMES[new Date(form.date).getDay()];
    const slots   = parsedAvail[dayName] ?? [];
    if (!slots.length) {
      toast.error(`${expert.user?.firstName} is not available on ${dayName}s`);
      return;
    }

    setLoading(true);
    try {
      const scheduledAt = new Date(`${form.date}T${form.time}`).toISOString();
      await bookingService.create({
        expertProfileId: expert.id,
        sessionType:     form.sessionType,
        scheduledAt,
        duration:        Number(form.duration),
        notes:           form.notes,
        sessionFocus:    form.sessionFocus || null,
      });
      toast.success('Booking request sent! The expert will confirm shortly.');
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Booking failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="absolute inset-0 bg-forest-900/60 backdrop-blur-sm" />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 space-y-5 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h3 className="font-serif text-xl text-forest-900">Book a Session</h3>
          <button onClick={onClose} className="text-forest-400 hover:text-forest-700 text-xl leading-none">✕</button>
        </div>
        <p className="text-sm text-forest-500">with <span className="font-medium text-forest-800">{name}</span></p>

        {/* Availability summary */}
        {hasAvail ? (
          <div className="bg-forest-50 border border-forest-100 rounded-xl px-4 py-3">
            <p className="text-xs font-medium text-forest-700 mb-1.5">Available days</p>
            <div className="flex flex-wrap gap-1.5">
              {availDayNames.map(d => (
                <span key={d} className="text-[11px] bg-forest-100 text-forest-700 px-2.5 py-1 rounded-full font-medium">{d}</span>
              ))}
            </div>
          </div>
        ) : (
          <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 text-xs text-amber-700">
            ⚠️ This expert hasn't set their availability yet. You can still send a request and they'll confirm a time.
          </div>
        )}

        <form onSubmit={handleBook} className="space-y-4">

          {/* Session type — only expert's offered types */}
          <div>
            <label className="block text-xs font-medium text-forest-700 mb-2">Session Type</label>
            <div className={`grid gap-2 ${offeredTypes.length === 1 ? 'grid-cols-1' : offeredTypes.length === 2 ? 'grid-cols-2' : 'grid-cols-3'}`}>
              {offeredTypes.map((t) => (
                <button key={t.id} type="button" onClick={() => setForm((f) => ({ ...f, sessionType: t.id }))}
                  className={`flex flex-col items-center gap-1.5 py-3 rounded-xl border-2 text-xs font-medium transition-all ${
                    form.sessionType === t.id
                      ? 'border-forest-700 bg-forest-50 text-forest-800'
                      : 'border-cream-200 text-forest-500 hover:border-forest-300'
                  }`}>
                  <t.icon size={16} />{t.label}
                </button>
              ))}
            </div>
          </div>

          {/* Date picker */}
          <div>
            <label className="block text-xs font-medium text-forest-700 mb-1.5">
              Select Date {hasAvail && <span className="text-forest-400 font-normal">(available days only)</span>}
            </label>
            {hasAvail ? (
              <select value={form.date} onChange={handleDateChange} required
                className="w-full px-3.5 py-2.5 rounded-xl border border-cream-300 text-sm text-forest-900 focus:outline-none focus:border-forest-500 bg-white">
                <option value="">Choose a date...</option>
                {availDates.slice(0, 30).map(d => {
                  const dt       = new Date(d + 'T00:00:00');
                  const dayLabel = dt.toLocaleDateString('en-GH', { weekday: 'long', day: 'numeric', month: 'short' });
                  return <option key={d} value={d}>{dayLabel}</option>;
                })}
              </select>
            ) : (
              <input type="date" required value={form.date} onChange={handleDateChange}
                min={new Date(Date.now() + 86400000).toISOString().split('T')[0]}
                className="w-full px-3.5 py-2.5 rounded-xl border border-cream-300 text-sm text-forest-900 focus:outline-none focus:border-forest-500 bg-white" />
            )}
          </div>

          {/* Time picker */}
          <div>
            <label className="block text-xs font-medium text-forest-700 mb-1.5">
              Select Time {form.date && hasAvail && <span className="text-forest-400 font-normal">(within expert's hours)</span>}
            </label>
            {hasAvail ? (
              <>
                <select value={form.time} onChange={upd('time')} required disabled={!form.date || timeSlots.length === 0}
                  className="w-full px-3.5 py-2.5 rounded-xl border border-cream-300 text-sm text-forest-900 focus:outline-none focus:border-forest-500 bg-white disabled:opacity-50 disabled:cursor-not-allowed">
                  <option value="">{!form.date ? 'Select a date first...' : timeSlots.length === 0 ? 'No slots available' : 'Choose a time...'}</option>
                  {timeSlots.map(t => {
                    const [h, m] = t.split(':').map(Number);
                    const ampm   = h >= 12 ? 'PM' : 'AM';
                    const h12    = h % 12 || 12;
                    return <option key={t} value={t}>{h12}:{String(m).padStart(2,'0')} {ampm}</option>;
                  })}
                </select>
                {form.date && timeSlots.length === 0 && (
                  <p className="text-xs text-amber-600 mt-1">No available slots for this date. Please select another day.</p>
                )}
              </>
            ) : (
              <input type="time" required value={form.time} onChange={upd('time')}
                className="w-full px-3.5 py-2.5 rounded-xl border border-cream-300 text-sm text-forest-900 focus:outline-none focus:border-forest-500 bg-white" />
            )}
          </div>

          {/* Duration */}
          <div>
            <label className="block text-xs font-medium text-forest-700 mb-1.5">Duration</label>
            <select value={form.duration} onChange={upd('duration')}
              className="w-full px-3.5 py-2.5 rounded-xl border border-cream-300 text-sm text-forest-900 focus:outline-none focus:border-forest-500 bg-white">
              {[30, 45, 60, 90, 120].map((d) => <option key={d} value={d}>{d} minutes</option>)}
            </select>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-xs font-medium text-forest-700 mb-1.5">Notes <span className="text-forest-400">(optional)</span></label>
            {/* Session Focus */}
            <div>
              <label className="block text-xs font-semibold text-forest-700 mb-1.5">
                Session Focus <span className="text-forest-400 font-normal">(optional)</span>
              </label>
              <select value={form.sessionFocus} onChange={upd('sessionFocus')}
                className="w-full px-3 py-2.5 rounded-xl border border-cream-300 text-sm focus:outline-none focus:border-forest-500 bg-white text-forest-700">
                <option value="">Select what you want to focus on...</option>
                {SESSION_FOCUS_CATEGORIES.map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
              <p className="text-[10px] text-forest-400 mt-1">This will appear on the expert's timesheet.</p>
            </div>

            <textarea value={form.notes} onChange={upd('notes')} rows={3}
              placeholder="What would you like to discuss in this session?"
              className="w-full px-3.5 py-2.5 rounded-xl border border-cream-300 text-sm text-forest-900 focus:outline-none focus:border-forest-500 bg-white resize-none" />
          </div>

          {/* Cost estimate */}
          {expert.hourlyRate && (
            <div className="bg-cream-50 border border-cream-200 rounded-xl px-4 py-3 flex justify-between text-sm">
              <span className="text-forest-500">Estimated cost</span>
              <span className="font-semibold text-forest-900">
                {expert.currency === 'USD' ? '$' : 'GH₵'}{((expert.hourlyRate / 60) * form.duration).toFixed(2)}
              </span>
            </div>
          )}

          {!user && (
            <p className="text-xs text-rust-500 text-center">
              <Link to="/login" className="underline">Sign in</Link> to book a session
            </p>
          )}

          <button type="submit" disabled={loading || !user || (hasAvail && (!form.date || !form.time || timeSlots.length === 0))}
            className="btn-primary w-full justify-center disabled:opacity-60 disabled:cursor-not-allowed">
            {loading
              ? <><span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Sending request...</>
              : 'Send Booking Request'}
          </button>
        </form>
      </div>
    </div>
  );
};

// ── Star Rating ───────────────────────────────────────────────
const StarRating = ({ rating, size = 14 }) => (
  <div className="flex items-center gap-0.5">
    {[1, 2, 3, 4, 5].map(star => (
      <Star key={star} size={size}
        className={star <= rating ? 'fill-amber-400 text-amber-400' : 'fill-cream-200 text-cream-200'} />
    ))}
  </div>
);

// ── Reviews Section ───────────────────────────────────────────
const ReviewsSection = ({ expertId }) => {
  const { data, loading } = useApi(() => expertService.getReviews(expertId), [expertId]);

  const reviews    = Array.isArray(data?.reviews) ? data.reviews : Array.isArray(data?.data?.reviews) ? data.data.reviews : Array.isArray(data) ? data : [];
  const avgRating  = data?.averageRating ?? data?.data?.averageRating ?? null;
  const totalCount = data?.totalCount    ?? data?.data?.totalCount    ?? reviews.length;

  if (loading) return (
    <div className="bg-white border border-cream-200 rounded-2xl p-6 animate-pulse space-y-4">
      <div className="h-5 bg-cream-200 rounded w-1/3" />
      {[1, 2].map(i => <div key={i} className="h-16 bg-cream-100 rounded-xl" />)}
    </div>
  );

  return (
    <div className="bg-white border border-cream-200 rounded-2xl p-6">
      <div className="flex items-center justify-between mb-5">
        <h3 className="font-serif text-lg font-semibold text-forest-900">
          Reviews {totalCount > 0 && <span className="text-sm font-normal text-forest-400 ml-2">({totalCount})</span>}
        </h3>
        {avgRating && (
          <div className="flex items-center gap-2">
            <StarRating rating={Math.round(avgRating)} />
            <span className="text-sm font-semibold text-forest-800">{Number(avgRating).toFixed(1)}</span>
          </div>
        )}
      </div>
      {avgRating && totalCount > 0 && (
        <div className="bg-forest-50 rounded-xl p-4 mb-5 flex items-center gap-6">
          <div className="text-center flex-shrink-0">
            <div className="font-serif text-4xl font-bold text-forest-900">{Number(avgRating).toFixed(1)}</div>
            <StarRating rating={Math.round(avgRating)} size={12} />
            <div className="text-xs text-forest-400 mt-1">{totalCount} review{totalCount !== 1 ? 's' : ''}</div>
          </div>
          <div className="flex-1 space-y-1.5">
            {[5, 4, 3, 2, 1].map(star => {
              const count = reviews.filter(r => r.rating === star).length;
              const pct   = totalCount > 0 ? (count / totalCount) * 100 : 0;
              return (
                <div key={star} className="flex items-center gap-2 text-xs text-forest-500">
                  <span className="w-3 text-right">{star}</span>
                  <Star size={10} className="fill-amber-400 text-amber-400 flex-shrink-0" />
                  <div className="flex-1 bg-cream-200 rounded-full h-1.5">
                    <div className="bg-amber-400 h-1.5 rounded-full transition-all" style={{ width: `${pct}%` }} />
                  </div>
                  <span className="w-4 text-right">{count}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}
      {reviews.length === 0 ? (
        <div className="text-center py-8 text-forest-400">
          <Star size={28} className="mx-auto mb-2 opacity-30" />
          <p className="text-sm">No reviews yet — be the first to review!</p>
        </div>
      ) : (
        <div className="space-y-4">
          {reviews.map((review, i) => {
            const clientName = review.clientProfile?.user
              ? `${review.clientProfile.user.firstName} ${review.clientProfile.user.lastName}`.trim()
              : 'Client';
            const initials = clientName.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
            const date     = review.createdAt ? new Date(review.createdAt).toLocaleDateString('en-GH', { month: 'short', year: 'numeric' }) : '';
            return (
              <div key={review.id ?? i} className="border-b border-cream-100 last:border-0 pb-4 last:pb-0">
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 rounded-full bg-forest-700 flex items-center justify-center text-cream-50 font-serif font-bold text-xs flex-shrink-0 overflow-hidden">
                    {review.clientProfile?.user?.avatar
                      ? <img src={review.clientProfile.user.avatar} alt={clientName} className="w-full h-full object-cover" />
                      : initials}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2 flex-wrap">
                      <div>
                        <span className="text-sm font-semibold text-forest-900">{clientName}</span>
                        {date && <span className="text-xs text-forest-400 ml-2">{date}</span>}
                      </div>
                      <StarRating rating={review.rating} size={12} />
                    </div>
                    {review.review && <p className="text-sm text-forest-600 leading-relaxed mt-1.5">{review.review}</p>}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const SkeletonProfile = () => (
  <div className="animate-pulse space-y-8 max-w-4xl mx-auto px-6 py-12">
    <div className="flex gap-6">
      <div className="w-24 h-24 bg-cream-200 rounded-2xl flex-shrink-0" />
      <div className="flex-1 space-y-3 pt-2">
        <div className="h-6 bg-cream-200 rounded w-1/2" />
        <div className="h-4 bg-cream-100 rounded w-1/3" />
        <div className="h-4 bg-cream-100 rounded w-2/3" />
      </div>
    </div>
    <div className="h-24 bg-cream-100 rounded-xl" />
    <div className="grid grid-cols-3 gap-4">{[1,2,3].map(i => <div key={i} className="h-20 bg-cream-100 rounded-xl" />)}</div>
  </div>
);

// ── Main Page ─────────────────────────────────────────────────
const ExpertProfilePage = () => {
  const { id } = useParams();
  const [showBooking, setShowBooking] = useState(false);
  const { data, loading, error } = useApi(() => expertService.getPublicProfile(id), [id]);
  const { data: availData }       = useApi(() => expertService.getAvailability(id), [id]);
  const expert = data?.expert ?? data;

  if (loading) return <div className="min-h-screen bg-cream-50"><Navbar /><SkeletonProfile /></div>;
  if (error || !expert) return (
    <div className="min-h-screen bg-cream-50">
      <Navbar />
      <div className="max-w-4xl mx-auto px-6 py-20 text-center">
        <AlertCircle size={40} className="mx-auto text-rust-400 mb-4" />
        <h2 className="font-serif text-2xl text-forest-900 mb-2">Expert not found</h2>
        <p className="text-forest-500 text-sm mb-6">This profile may have been removed or doesn't exist.</p>
        <Link to="/experts" className="btn-primary inline-flex">← Browse Experts</Link>
      </div>
      <Footer />
    </div>
  );

  const name     = `${expert.user?.firstName ?? ''} ${expert.user?.lastName ?? ''}`.trim();
  const initials = `${expert.user?.firstName?.[0] ?? ''}${expert.user?.lastName?.[0] ?? ''}`;
  const specs    = Array.isArray(expert.specializations) ? expert.specializations : [];
  const quals    = Array.isArray(expert.qualifications)  ? expert.qualifications  : [];
  const rate     = expert.hourlyRate ? `${expert.currency === 'USD' ? '$' : 'GH₵'}${expert.hourlyRate}/hr` : 'Rate on request';

  const levelLabels       = (expert.expertLevels ?? expert.levelAssignments ?? []).map(la => la.level?.name).filter(Boolean);
  const expertSessionTypes = parseSessionTypes(expert.sessionTypes);
  const offeredTypes       = SESSION_TYPES.filter(t => expertSessionTypes.includes(t.id));

  const DAYS       = ['monday','tuesday','wednesday','thursday','friday','saturday','sunday'];
  const availability = availData?.availability ?? {};

  return (
    <div className="min-h-screen bg-cream-50">
      <Navbar />
      {showBooking && (
        <BookingModal
          expert={expert}
          availability={availData?.availability ?? availData?.data?.availability ?? {}}
          expertSessionTypes={expertSessionTypes}
          onClose={() => setShowBooking(false)}
        />
      )}
      <div className="pt-24 pb-16">
        <div className="max-w-5xl mx-auto px-6 lg:px-10">
          <Link to="/experts" className="inline-flex items-center gap-2 text-sm text-forest-500 hover:text-forest-800 mb-8 transition-colors">
            <ChevronLeft size={16} /> Back to Experts
          </Link>
          <div className="grid lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2 space-y-6">

              {/* Header card */}
              <div className="bg-white border border-cream-200 rounded-2xl p-6">
                <div className="flex items-start gap-5">
                  <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-forest-600 to-forest-800 text-white flex items-center justify-center font-serif font-bold text-2xl flex-shrink-0">
                    {expert.user?.avatar
                      ? <img src={expert.user.avatar} alt={name} className="w-full h-full object-cover rounded-2xl" />
                      : initials}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h1 className="font-serif text-2xl font-semibold text-forest-900">{name}</h1>
                    <p className="text-sm text-forest-500 mt-1">{specs.slice(0, 3).join(' · ')}</p>
                    <div className="flex flex-wrap items-center gap-4 mt-3 text-xs text-forest-500">
                      <span className="flex items-center gap-1"><Clock size={11} />{expert.yearsOfExperience} yrs experience</span>
                      {expert.averageRating > 0 && (
                        <span className="flex items-center gap-1.5">
                          <StarRating rating={Math.round(expert.averageRating)} size={12} />
                          <span className="font-medium text-forest-700">{Number(expert.averageRating).toFixed(1)}</span>
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* About */}
              {expert.bio && (
                <div className="bg-white border border-cream-200 rounded-2xl p-6">
                  <h3 className="font-serif text-lg font-semibold text-forest-900 mb-3">About</h3>
                  <p className="text-sm text-forest-600 leading-relaxed">{expert.bio}</p>
                </div>
              )}

              {/* Specializations */}
              {specs.length > 0 && (
                <div className="bg-white border border-cream-200 rounded-2xl p-6">
                  <h3 className="font-serif text-lg font-semibold text-forest-900 mb-3">Specializations</h3>
                  <div className="flex flex-wrap gap-2">
                    {specs.map(s => <span key={s} className="text-xs font-medium bg-forest-50 text-forest-700 border border-forest-100 px-3 py-1 rounded-full">{s}</span>)}
                  </div>
                </div>
              )}

              {/* Qualifications */}
              {quals.length > 0 && (
                <div className="bg-white border border-cream-200 rounded-2xl p-6">
                  <h3 className="font-serif text-lg font-semibold text-forest-900 mb-3">Qualifications</h3>
                  <ul className="space-y-2">
                    {quals.map((q, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-forest-600">
                        <span className="w-1.5 h-1.5 bg-forest-500 rounded-full mt-1.5 flex-shrink-0" />{q}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Levels */}
              {levelLabels.length > 0 && (
                <div className="bg-white border border-cream-200 rounded-2xl p-6">
                  <h3 className="font-serif text-lg font-semibold text-forest-900 mb-3">Levels Covered</h3>
                  <div className="flex flex-wrap gap-2">
                    {levelLabels.map(l => <span key={l} className="text-xs font-medium bg-cream-100 text-forest-700 border border-cream-200 px-3 py-1 rounded-full">{l}</span>)}
                  </div>
                </div>
              )}

              {/* Availability */}
              {Object.keys(availability).length > 0 && (
                <div className="bg-white border border-cream-200 rounded-2xl p-6">
                  <h3 className="font-serif text-lg font-semibold text-forest-900 mb-4 flex items-center gap-2">
                    <Calendar size={17} /> Weekly Availability
                  </h3>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    {DAYS.map((day) => {
                      const slots = availability[day] ?? [];
                      return (
                        <div key={day} className={`rounded-xl p-3 text-xs ${slots.length > 0 ? 'bg-forest-50 border border-forest-100' : 'bg-cream-50 border border-cream-100'}`}>
                          <div className={`font-medium capitalize mb-1.5 ${slots.length > 0 ? 'text-forest-700' : 'text-forest-300'}`}>{day}</div>
                          {slots.length > 0
                            ? slots.map((s, i) => <div key={i} className="text-forest-500">{s.start} – {s.end}</div>)
                            : <div className="text-forest-300">Unavailable</div>}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              <ReviewsSection expertId={id} />
            </div>

            {/* Sticky booking sidebar */}
            <div className="space-y-4">
              <div className="bg-white border border-cream-200 rounded-2xl p-6 sticky top-24">
                <div className="text-center mb-5">
                  <div className="font-serif text-xl font-bold text-forest-900">{rate}</div>
                  {expert.timezone && <div className="text-xs text-forest-400 mt-1">Timezone: {expert.timezone}</div>}
                </div>

                {/* Session types — only what the expert offers */}
                <div className="space-y-2 mb-5">
                  {SESSION_TYPES.map((t) => {
                    const offered = expertSessionTypes.includes(t.id);
                    return (
                      <div key={t.id} className={`flex items-center gap-2.5 text-sm ${offered ? 'text-forest-600' : 'text-forest-300'}`}>
                        <t.icon size={14} className={offered ? 'text-forest-500' : 'text-forest-200'} />
                        <span className={offered ? '' : 'line-through'}>{t.label}</span>
                        {offered && <span className="ml-auto text-[10px] text-forest-400 font-medium">available</span>}
                      </div>
                    );
                  })}
                </div>

                <button onClick={() => setShowBooking(true)} className="btn-primary w-full justify-center">Book a Session</button>
                {expert.linkedinUrl && (
                  <a href={expert.linkedinUrl} target="_blank" rel="noopener noreferrer"
                    className="btn-outline w-full justify-center mt-3 text-sm">View LinkedIn</a>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default ExpertProfilePage;